import React, { useMemo, useRef } from 'react'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react'

import { cn } from '@/lib/utils'
import { useDropdownPosition } from '@/lib/hooks/useDropdownPosition'
import Icon, { IconTypes } from '@/components/Icon'
import LcIcon from '@/components/livecontact/icons'

export type SelectItem = {
  title: string
  value: string
  color?: string
  icon?: IconTypes
}

type SelectValue = SelectItem | string

type Props<TItem extends SelectValue = SelectValue> = {
  label?: string
  title?: string
  icon?: IconTypes
  className?: string
  classButton?: string
  classArrow?: string
  classOptions?: string
  classOption?: string
  classIcon?: string
  classLabel?: string
  placeholder?: string
  items?: readonly TItem[]
  value?: TItem | null
  onChange: (item: TItem) => void
  small?: boolean
}

type SelectOptionsProps = {
  isOpen: boolean
  triggerRef: React.RefObject<HTMLButtonElement>
  items?: SelectItem[]
  small?: boolean
  classOptions?: string
  renderOption: (item: SelectItem) => React.ReactElement
}

const normalizeItem = (item: SelectValue): SelectItem => {
  if (typeof item === 'string') {
    return {
      title: item,
      value: item,
    }
  }

  return item
}

function SelectOptionsList({
  isOpen,
  triggerRef,
  items,
  small,
  classOptions,
  renderOption,
}: SelectOptionsProps) {
  const position = useDropdownPosition({
    isOpen,
    triggerRef,
    dropdownHeight: 256,
  })

  return (
    <Transition leave="transition-opacity duration-[120ms] ease-out-strong" leaveFrom="opacity-100" leaveTo="opacity-0">
      <ListboxOptions
        className={cn(
          'absolute left-0 right-0 z-20 w-full rounded-[12px] bg-surface p-1.5 shadow-pop outline-none',
          'transition-[opacity,transform] duration-[180ms] ease-out-strong data-[closed]:scale-[0.97] data-[closed]:opacity-0',
          small && 'right-auto min-w-[180px]',
          position === 'top' ? 'bottom-full mb-1.5 origin-bottom-left' : 'top-full mt-1.5 origin-top-left',
          classOptions
        )}
      >
        {items?.map(renderOption)}
      </ListboxOptions>
    </Transition>
  )
}

const Select = <TItem extends SelectValue = SelectValue>({
  label,
  title,
  icon,
  className,
  classButton,
  classArrow,
  classOptions,
  classOption,
  classIcon,
  classLabel,
  placeholder,
  items,
  value,
  onChange,
  small,
}: Props<TItem>) => {
  const triggerRef = useRef<HTMLButtonElement>(null)

  const normalizedItems = useMemo<SelectItem[]>(() => {
    return items?.map((item) => normalizeItem(item)) ?? []
  }, [items])

  // Always a defined SelectItem so Headless UI never transitions from uncontrolled → controlled.
  // An empty `value` string signals "nothing selected" and is used in place of null/undefined.
  const normalizedValue = useMemo<SelectItem>(() => {
    if (!value) return { title: '', value: '' }

    return normalizeItem(value)
  }, [value])

  const hasValue = normalizedValue.value !== ''

  const handleChange = (selectedItem: SelectItem) => {
    const originalItem = items?.find((item) => {
      const normalizedItem = normalizeItem(item)

      return normalizedItem.value === selectedItem.value
    })

    if (!originalItem) return

    onChange(originalItem)
  }

  const renderOption = (item: SelectItem) => (
    <ListboxOption
      key={item.value}
      value={item}
      className={cn(
        'flex cursor-pointer items-center rounded-[8px] px-2.5 py-2 text-[14px] font-medium leading-[1.2] tracking-[-0.2px] text-ink-2 outline-none',
        'transition-[background-color,color,transform] duration-[120ms] ease-out-strong active:scale-[0.99] ui-active:bg-surface-2 ui-active:text-ink ui-selected:text-ink dark:ui-active:bg-surface-3',
        small && 'py-1.5',
        classOption
      )}
    >
      {item.color && (
        <div
          className="shrink-0 w-3.5 h-3.5 mt-1 ml-1 mr-4 rounded"
          style={{ backgroundColor: item.color }}
        />
      )}

      {item.icon && (
        <span className="mr-2.5 inline-flex shrink-0 text-ink-4">
          <Icon className="size-4 fill-current" name={item.icon} />
        </span>
      )}

      <div className="mr-auto">{item.title}</div>

      {!small && (
        <span className="ml-2 hidden shrink-0 text-brand ui-selected:inline-flex">
          <LcIcon name="check" size={14} strokeWidth={2} />
        </span>
      )}
    </ListboxOption>
  )

  return (
    <div className={cn('relative', className)}>
      {label && (
        <div className={cn('mb-1.5 flex text-[12px] font-medium leading-[1.4] tracking-[-0.2px] text-ink-3', classLabel)}>
          {label}
        </div>
      )}

      <Listbox value={normalizedValue} onChange={handleChange} by="value">
        {({ open }) => (
          <div>
            <ListboxButton
              ref={triggerRef}
              className={cn(
                'flex h-9 w-full items-center rounded-[8px] bg-surface px-3 text-left text-[14px] leading-[1.4] tracking-[-0.2px] text-ink shadow-input outline-none',
                'transition-[box-shadow,transform] duration-[160ms] ease-out-strong active:scale-[0.99]',
                'data-[open]:shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_rgb(var(--lc-brand)),0_0_0_4px_rgb(var(--lc-brand)/0.14)] focus-visible:shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_rgb(var(--lc-brand)),0_0_0_4px_rgb(var(--lc-brand)/0.14)]',
                small && 'h-8 pr-2',
                classButton
              )}
            >
              {title && (
                <div className="mr-2 shrink-0 border-r border-line pr-2 text-ink-4">{title}</div>
              )}

              {icon && (
                <span className={cn('mr-2 inline-flex shrink-0 text-ink-4', small && 'mr-1.5', classIcon)}>
                  <Icon className="size-4 fill-current" name={icon} />
                </span>
              )}

              {hasValue && normalizedValue.color && (
                <div
                  className="shrink-0 w-3.5 h-3.5 ml-1 mr-4 rounded"
                  style={{ backgroundColor: normalizedValue.color }}
                />
              )}

              {hasValue && normalizedValue.icon && (
                <span className="mr-2.5 inline-flex shrink-0 text-ink-2">
                  <Icon className="size-4 fill-current" name={normalizedValue.icon} />
                </span>
              )}

              <span className={cn('mr-auto truncate', small && 'font-medium')}>
                {hasValue ? normalizedValue.title : <span className="text-ink-4">{placeholder}</span>}
              </span>

              <LcIcon
                name="chevron-down"
                className={cn(
                  'ml-2 shrink-0 text-ink-4 transition-transform duration-[180ms] ease-out-strong',
                  open && 'rotate-180 text-ink-2',
                  small && 'ml-1',
                  classArrow
                )}
              />
            </ListboxButton>

            <SelectOptionsList
              isOpen={open}
              triggerRef={triggerRef}
              items={normalizedItems}
              small={small}
              classOptions={classOptions}
              renderOption={renderOption}
            />
          </div>
        )}
      </Listbox>
    </div>
  )
}

export default Select
